import type { DurationOption } from '../models/calendar'

/** Max meeting duration in minutes (12 hours). */
const MAX_DURATION_MINUTES = 12 * 60

/**
 * Backend contract: `duration` / `slotDuration` numeric fields are always stored
 * in minutes (never seconds). `durationUnit` / `slotDurationUnit` only controls
 * how the UI displays the value (hours vs mins).
 */

function normalizeDurationUnit(unit: string): 'hours' | 'mins' {
  return unit === 'hours' ? 'hours' : 'mins'
}

/**
 * Repair legacy double-conversion where minutes were multiplied by 60 twice
 * (e.g. 720 min → 43200). Does not treat values as seconds.
 */
export function normalizeApiMinutes(rawMinutes: number): number {
  if (
    rawMinutes > MAX_DURATION_MINUTES * 2 &&
    rawMinutes % 60 === 0 &&
    rawMinutes / 60 <= MAX_DURATION_MINUTES
  ) {
    return rawMinutes / 60
  }
  return rawMinutes
}

/** Form display -> total minutes for validation and increment logic. */
export function getFormDurationInMinutes(
  duration: number,
  durationUnit: string
): number {
  const val = parseInt(String(duration), 10) || 0
  if (normalizeDurationUnit(durationUnit) === 'hours') {
    return val * 60
  }
  return val
}

function getDurationInMinutes(opt: DurationOption): number {
  return getFormDurationInMinutes(opt.duration, opt.durationUnit)
}

/** API minutes + unit -> form display values. */
export function parseDurationValueForForm(
  rawDuration: number,
  rawUnit: string
): { duration: number; durationUnit: 'hours' | 'mins' } {
  const durationUnit = normalizeDurationUnit(rawUnit)
  const minutes = normalizeApiMinutes(Number(rawDuration) || 0)

  if (minutes <= 0) {
    return { duration: minutes, durationUnit }
  }

  if (durationUnit === 'hours') {
    return { duration: minutes / 60, durationUnit: 'hours' }
  }

  return { duration: minutes, durationUnit: 'mins' }
}

/** Form display -> API payload (duration field always in minutes). */
export function serializeDurationValueForApi(
  displayDuration: number,
  displayUnit: string
): { duration: number; durationUnit: 'hours' | 'mins' } {
  const durationUnit = normalizeDurationUnit(displayUnit)
  const display = parseInt(String(displayDuration), 10) || 0

  const minutes = durationUnit === 'hours' ? display * 60 : display

  return { duration: minutes, durationUnit }
}

export function parseSlotDurationForForm(
  slotDuration: number | undefined,
  slotDurationUnit: string | undefined
): { duration: string; durationUnit: string } {
  const parsed = parseDurationValueForForm(
    Number(slotDuration) || 0,
    slotDurationUnit || 'mins'
  )

  return {
    duration: parsed.duration ? String(parsed.duration) : '',
    durationUnit: parsed.durationUnit,
  }
}

function isEmptyDuration(opt: DurationOption): boolean {
  const raw = String(opt.duration).trim()
  return !raw || Number.isNaN(parseInt(raw, 10)) || parseInt(raw, 10) === 0
}

type RawDurationOption = Record<string, unknown>

export function getDurationOptionsFromCalendar(
  calendar: {
    durationOptions?: unknown
    data?: Record<string, unknown>
  } | null | undefined
): unknown {
  if (!calendar) {
    return []
  }

  const calendarRecord = calendar as Record<string, unknown>
  const data = calendarRecord.data

  return (
    calendarRecord.durationOptions ??
    (data && typeof data === 'object' ? data.durationOptions : undefined) ??
    (data && typeof data === 'object' ? data.duration_options : undefined) ??
    calendarRecord.duration_options ??
    []
  )
}

export function parseDurationOptionsForForm(rawOptions: unknown): DurationOption[] {
  if (!Array.isArray(rawOptions) || rawOptions.length === 0) {
    return []
  }

  return rawOptions
    .map((rawOption, index) => {
      const opt = rawOption as RawDurationOption
      const rawUnit = String(opt.durationUnit ?? opt.duration_unit ?? 'mins')
      const rawDuration = Number(opt.duration ?? 0)
      const { duration, durationUnit } = parseDurationValueForForm(
        rawDuration,
        rawUnit
      )

      return {
        duration,
        durationUnit,
        isDefault: Boolean(opt.isDefault ?? opt.is_default ?? false),
        order: Number(opt.order ?? index + 1),
      }
    })
    .sort((a, b) => a.order - b.order)
    .map((opt, index) => ({ ...opt, order: index + 1 }))
}

export function serializeDurationOptionForApi(opt: DurationOption): DurationOption {
  const serialized = serializeDurationValueForApi(opt.duration, opt.durationUnit)

  return {
    duration: serialized.duration,
    durationUnit: serialized.durationUnit,
    isDefault: opt.isDefault,
    order: opt.order,
  }
}

export function getInvalidDurationOptionIndices(
  options: DurationOption[]
): number[] {
  const invalidIndices = new Set<number>()

  options.forEach((opt, index) => {
    if (isEmptyDuration(opt)) {
      invalidIndices.add(index)
    }
  })
  if (invalidIndices.size > 0) {
    return [...invalidIndices]
  }

  options.forEach((opt, index) => {
    const mins = getDurationInMinutes(opt)
    if (mins < 1 || mins > 12 * 60) {
      invalidIndices.add(index)
    }
  })
  if (invalidIndices.size > 0) {
    return [...invalidIndices]
  }

  const seen = new Map<number, number>()
  options.forEach((opt, index) => {
    const mins = getDurationInMinutes(opt)
    const firstIndex = seen.get(mins)
    if (firstIndex !== undefined) {
      invalidIndices.add(firstIndex)
      invalidIndices.add(index)
    } else {
      seen.set(mins, index)
    }
  })

  return [...invalidIndices]
}

export function getDurationOptionsValidationMessage(
  options: DurationOption[],
  t: (key: string) => string
): string | null {
  if (options.some(isEmptyDuration)) {
    return t('calendar_advanced.availability.empty_duration_error')
  }

  const hasInvalidRange = options.some((opt) => {
    const mins = getDurationInMinutes(opt)
    return mins < 1 || mins > 12 * 60
  })
  if (hasInvalidRange) {
    return t('calendar_advanced.availability.meeting_duration_error')
  }

  const normalized = options.map(getDurationInMinutes)
  if (new Set(normalized).size !== normalized.length) {
    return t('calendar_advanced.availability.duplicate_duration_error')
  }

  return null
}

export function canAddDurationOption(
  options: DurationOption[],
  maxOptions: number,
  currentDuration?: string,
  currentDurationUnit?: string
): boolean {
  if (options.length >= maxOptions) {
    return false
  }

  if (options.length === 0) {
    const duration = parseInt(currentDuration || '0', 10) || 0
    const durationUnit = currentDurationUnit || 'mins'
    return (
      getInvalidDurationOptionIndices([
        { duration, durationUnit, isDefault: true, order: 1 },
      ]).length === 0
    )
  }

  return getInvalidDurationOptionIndices(options).length === 0
}
